import React, { useEffect, useState } from "react";
import { Country, State, City, ICountry, IState, ICity } from "country-state-city";
import axios from "axios";
import { Autocomplete, FormControl, Grid, TextField, Typography } from "@mui/material";
import { Label } from "../ui/label";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { Input } from "../ui/input";
import { GOOGLE_MAPS_KEY } from "@/config";
import { useCustomLoader } from "../Loader";
import { cn } from "@/lib/utils";

type Props = {
    control: Control<any>;
    errors: any;
    setValue: any;
    toast: any
    setError: any
    getValues: any
};

export const LocationSelector: React.FC<Props> = ({ control, errors, setValue, setError, toast, getValues }) => {
    const loader = useCustomLoader();
    const [pinCode, setPinCode] = useState<string>("");
    const [countries, setCountries] = useState<ICountry[]>([]);
    const [states, setStates] = useState<IState[]>([]);
    const [cities, setCities] = useState<ICity[]>([]);


    useEffect(() => {
        setCountries(Country.getAllCountries());
    }, []);

    useEffect(() => {
        setPinCode(getValues("address.postalCode"));
    }, [getValues("address.postalCode")])

    const handlePincodeSearch = (pincode: string) => {
        const timer = setTimeout(async () => {
            try {
                loader(true);
                // Example using Google Maps Geocoding API
                const res = await axios.get(
                    `https://maps.googleapis.com/maps/api/geocode/json?address=${pincode}&key=${GOOGLE_MAPS_KEY}`
                );
                const result = res.data.results[0];
                if (!result) return;

                const components = result.address_components;
                const countryName = components.find((c: any) => c.types.includes("country"))?.long_name;
                const stateName = components.find((c: any) => c.types.includes("administrative_area_level_1"))?.long_name;
                const cityName = components.find((c: any) => c.types.includes("locality"))?.long_name;

                const country = countries.find((c) => c.name === countryName);
                const state = country ? State.getStatesOfCountry(country.isoCode).find((s) => s.name === stateName) : undefined;
                const city = (country && state)
                    ? City.getCitiesOfState(country.isoCode, state.isoCode).find((ct) => ct.name === cityName)
                    : undefined;

                setStates(country ? State.getStatesOfCountry(country.isoCode) : []);
                setCities(country && state ? City.getCitiesOfState(country.isoCode, state.isoCode) : []);

                setTimeout(() => {
                    setValue("address.country", country?.name);
                    setValue("address.state", state?.name);
                    setValue("address.city", city?.name);

                    if (country?.name) setError("address.country", { message: "" });
                    if (state?.name) setError("address.state", { message: "" });
                    if (city?.name) setError("address.city", { message: "" });
                }, 1000);

                // onChange({ country, state, city, pincode });
            } catch (err) {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: "Pincode not found",
                })
            }
            finally {
                loader(false);
            }
        }, 600);

        return () => clearTimeout(timer);
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            if (pinCode) {
                handlePincodeSearch(pinCode);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [pinCode])

    return (
        <>
            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                <Controller
                    name="address.postalCode"
                    control={control}

                    render={({ field }) => (
                        <>
                            <TextField
                                fullWidth
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    setPinCode(e.target.value);
                                }}
                                label="PostalCode"
                                placeholder="Please enter postal code"
                            />
                        </>
                    )}
                />
                {
                    errors.address?.postalCode && (
                        <Typography sx={{ color: "red" }}>{String(errors.address?.postalCode.message)}</Typography>
                    )
                }

            </Grid>
            {/* Country Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="address.country"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Autocomplete
                                options={countries}
                                getOptionLabel={(option) => option.name}
                                value={countries.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                    setStates(newValue ? State.getStatesOfCountry(newValue.isoCode) : []);
                                    setCities([]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Country"
                                        id="country"
                                        label="Country"
                                    />
                                )}
                            />
                            {errors.address?.country && (
                                <Typography sx={{ color: "red" }}>
                                    {String(errors.address?.country.message)}
                                </Typography>
                            )}
                        </>
                    )}
                />
            </Grid>


            {/* State Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="address.state"
                    control={control}
                    render={({ field }) => (
                        <FormControl>
                            <Autocomplete
                                options={states}
                                getOptionLabel={(option) => option.name}
                                value={states.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                    setCities(newValue ? City.getCitiesOfState(newValue.countryCode, newValue.isoCode) : []);
                                }}
                                renderInput={(params) => (
                                    <div ref={params.InputProps.ref}>
                                        <TextField
                                            {...params}
                                            placeholder="Select State"
                                            id="state"
                                            label="State"
                                        />
                                    </div>
                                )}
                            />
                            {errors.address?.state && (
                                <Typography sx={{ color: "red" }}>{String(errors.address?.state.message)}</Typography>
                            )}
                        </FormControl>
                    )}
                />
            </Grid>

            {/* City Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="address.city"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.name}
                                value={cities.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                }}
                                renderInput={(params) => (
                                    <div ref={params.InputProps.ref}>
                                        <TextField
                                            {...params}
                                            placeholder="Select City"
                                            id="city"
                                            label="City"
                                        />
                                    </div>
                                )}
                            />
                            {errors.address?.city && (
                                <Typography sx={{ color: "red" }}>{String(errors.address?.city.message)}</Typography>
                            )}
                        </>
                    )}
                />
            </Grid>
        </>
    );
};
